import { useEffect, useMemo, useState } from 'react'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useQntmStorageList, qntmStorageListQueryKey } from 'src/Libs/installation-details/hooks/use-qntm-storage-list'
import { usePortalMutation } from 'src/Libs/api'
import { createQntmStorageDeviceLink, deleteDeviceLink } from 'src/Libs/installations'
import { useQueryClient } from '@tanstack/react-query'
import { useDeviceDetectorScan } from 'src/Libs/installations/hooks/installation-configuration/storage/use-device-detector'
import { isQntmFoundDevice } from 'src/Libs/installations/utils/device-detector'

export type SerialStatus = 'new' | 'missing' | 'unchanged'

export interface SerialWithStatus {
  serialNumber: string
  status: SerialStatus
  isSelected: boolean
}

interface UseScanNetworkDialogParams {
  onClose?: () => void
  emcSerialnumber: string
  installationId: number
  emcDeviceLinkId: number
}

interface UseScanNetworkDialogResult {
  serialList: SerialWithStatus[]
  error: unknown
  hasScanError: boolean
  isSaving: boolean
  isSynchronizing: boolean
  isScanning: boolean
  hasScanResults: boolean
  handleCancel: () => void
  handleRetryScan: () => void
  handleSave: () => Promise<void>
}

const normalizeSerialNumber = (serialNumber: string): string => serialNumber.trim()

export const useScanNetworkDialog = ({
  onClose,
  emcSerialnumber,
  installationId,
  emcDeviceLinkId,
}: UseScanNetworkDialogParams): UseScanNetworkDialogResult => {
  const authFetch = useAuthFetchWithResponse()
  const queryClient = useQueryClient()
  const [selectedSerials, setSelectedSerials] = useState<Set<string>>(new Set())
  const [isSynchronizing, setIsSynchronizing] = useState(false)

  const {
    data: linkedQntmStorages,
    isLoading: linkedQntmStoragesLoading,
    isError: linkedQntmStoragesError,
  } = useQntmStorageList(installationId.toString(), emcDeviceLinkId)
  const { scanStatus, scanResult, isScanning, hasCompleted, error, startScan, stopScan } = useDeviceDetectorScan(emcSerialnumber)

  const createDeviceLinkMutation = usePortalMutation<void, unknown, { serialNumber: string }>({
    mutationFn: async ({ serialNumber }) => {
      return createQntmStorageDeviceLink(authFetch, installationId, emcDeviceLinkId, {
        deviceName: serialNumber,
        type: 'qntm',
        serial: emcSerialnumber,
      })
    },
  })

  const deleteDeviceLinkMutation = usePortalMutation<void, unknown, { deviceLinkId: number }>({
    mutationFn: async ({ deviceLinkId }) => {
      return deleteDeviceLink(installationId, deviceLinkId.toString(), authFetch)
    },
  })

  const isSaving = createDeviceLinkMutation.isPending || deleteDeviceLinkMutation.isPending
  const hasScanError = (error !== null && !isScanning && !hasCompleted) || scanStatus?.status === 'error'

  const linkedDevicesBySerial = useMemo(() => {
    const serialToDeviceLink = new Map<string, number>()

    for (const device of linkedQntmStorages ?? []) {
      const normalizedSerial = normalizeSerialNumber(device.deviceName)
      if (!normalizedSerial || serialToDeviceLink.has(normalizedSerial)) {
        continue
      }

      serialToDeviceLink.set(normalizedSerial, device.deviceLinkId)
    }

    return serialToDeviceLink
  }, [linkedQntmStorages])

  const scanSerials = useMemo(() => {
    if (!hasCompleted) {
      return null
    }

    const serials = (scanResult?.devices ?? [])
      .filter(isQntmFoundDevice)
      .map(device => normalizeSerialNumber(device.serialNumber))
      .filter(serial => serial.length > 0)

    return Array.from(new Set(serials)).sort((a, b) => a.localeCompare(b))
  }, [hasCompleted, scanResult?.devices])

  useEffect(() => {
    void startScan()

    return () => {
      stopScan()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emcSerialnumber])

  useEffect(() => {
    if (scanSerials === null) return
    setSelectedSerials(new Set(scanSerials))
  }, [scanSerials])

  const serialList = useMemo((): SerialWithStatus[] => {
    if (scanSerials === null) return []

    const linkedSerials = Array.from(linkedDevicesBySerial.keys())
    const linkedSet = new Set(linkedSerials)
    const scanSet = new Set(scanSerials)
    const allSerials = Array.from(new Set([...linkedSerials, ...scanSerials])).sort((a, b) => a.localeCompare(b))

    return allSerials.map(serialNumber => {
      const isInScan = scanSet.has(serialNumber)
      const isLinked = linkedSet.has(serialNumber)

      let status: SerialStatus = 'unchanged'
      if (isInScan && !isLinked) {
        status = 'new'
      } else if (!isInScan && isLinked) {
        status = 'missing'
      }

      return {
        serialNumber,
        status,
        isSelected: selectedSerials.has(serialNumber),
      }
    })
  }, [linkedDevicesBySerial, scanSerials, selectedSerials])

  const handleCancel = (): void => {
    stopScan()
    onClose?.()
  }

  const handleSave = async (): Promise<void> => {
    if (scanSerials === null || linkedQntmStoragesLoading || linkedQntmStoragesError) {
      handleCancel()
      return
    }

    const linkedEntries = Array.from(linkedDevicesBySerial.entries())
    const serialsToCreate = Array.from(selectedSerials).filter(serialNumber => !linkedDevicesBySerial.has(serialNumber))
    const linkIdsToDelete = linkedEntries.filter(([serialNumber]) => !selectedSerials.has(serialNumber)).map(([, deviceLinkId]) => deviceLinkId)

    try {
      setIsSynchronizing(true)

      const syncResults = await Promise.allSettled([
        ...serialsToCreate.map(serialNumber => createDeviceLinkMutation.mutateAsync({ serialNumber })),
        ...linkIdsToDelete.map(deviceLinkId => deleteDeviceLinkMutation.mutateAsync({ deviceLinkId })),
      ])

      await queryClient.invalidateQueries({
        queryKey: qntmStorageListQueryKey(installationId.toString(), emcDeviceLinkId),
      })

      const hasSyncError = syncResults.some(result => result.status === 'rejected')
      if (hasSyncError) {
        console.error('Failed to sync qntm device links after scan')
        return
      }
      handleCancel()
    } catch (syncError) {
      console.error('Failed to refresh qntm device links after scan sync', syncError)
    } finally {
      setIsSynchronizing(false)
    }
  }

  const handleRetryScan = (): void => {
    void startScan()
  }

  return {
    serialList,
    error,
    hasScanError,
    isSaving,
    isSynchronizing,
    isScanning,
    hasScanResults: scanSerials !== null,
    handleCancel,
    handleRetryScan,
    handleSave,
  }
}
